import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAudioContext, getMasterGain, secondsPerBar } from '@/audio/AudioEngine';
import { MonoVoice, playDrumHit, playNote } from '@/audio/instrumentVoices';
import { getKits, getOneShotCategories, getOneShots, getSoundBuffer, loadProducerSounds } from '@/audio/producerSounds';
import { samplerDefaultsForSound } from '@/audio/sampler';
import { foldPitchToKey, parseSessionKey } from '@/audio/musicTheory';
import { scheduleMetronomeBeats, startPlayback, stopPlayback } from '@/audio/PlaybackScheduler';
import { applyRecordingEvents, clipRelativeBeat, DOCK_NOTE_OFFSETS, DRUM_KEY_ROWS, DRUM_MIDI_ROWS, isTextEntry, midiMessage, prepareRecordingTarget, recordableTrackType, targetPatternClip } from '@/audio/liveInput';
import { countTrackSlots } from '@/state/classSession';

const CLICK_KEY = 'ba_transport_click';
const AUTO_QUANTIZE_KEY = 'ba_transport_auto_quantize';
const COUNT_IN_KEY = 'ba_transport_count_in';
const readClick = () => { try { return localStorage.getItem(CLICK_KEY) === '1'; } catch { return false; } };
export const readAutoQuantize = () => { try { return localStorage.getItem(AUTO_QUANTIZE_KEY) !== '0'; } catch { return true; } };
export const readCountIn = () => { try { return localStorage.getItem(COUNT_IN_KEY) !== '0'; } catch { return true; } };

/** Identity of the sound a mono voice was built for. A different value means the old voice is stale. */
export function liveVoiceKey(type, clip) {
  if (!type || !clip?.pattern) return '';
  const p = clip.pattern;
  return [clip.loop_id, type, p.voice, p.sampleId, p.rootNote, p.tune, p.fine, p.mono, p.glide, p.attack, p.release, p.reverb, p.echo, p.gate].join('|');
}

export function useLiveInput({ tracks, sectionMarkers, classSession, pushState, bpm, totalBars, playheadBar, loopEnabled, loopRegion, keySignature, maxTracks,
  onPlayingChange, onPlayheadUpdate, onPlaybackEnd, onNotice } = {}) {
  const [armedTrackId, setArmedTrackId] = useState(null);
  const [dockOpen, setDockOpen] = useState(false);
  const [octave, setOctave] = useState(3);
  const [velocityLevel, setVelocityLevel] = useState(3);
  const [heldKeys, setHeldKeys] = useState(() => new Set());
  const [midiName, setMidiName] = useState('');
  const [recording, setRecording] = useState(false);
  const [sessionTracks, setSessionTracks] = useState(null);
  const [clickEnabled, setClickEnabledState] = useState(readClick);
  const [autoQuantize, setAutoQuantizeState] = useState(readAutoQuantize);
  const [countInEnabled, setCountInEnabledState] = useState(readCountIn);
  const recordSnap = '1/16';
  const takeRef = useRef(null);
  const heldRef = useRef(new Map());
  const auditionsRef = useRef(new Map());
  // token -> the pitch that actually sounded on the mono voice (Lock to key can fold it), so release always matches.
  const monoTokensRef = useRef(new Map());
  // token -> { released } while a key's sound is still loading, so a quick tap is not lost or left ringing.
  const pendingRef = useRef(new Map());
  const midiAccessRef = useRef(null);
  const latestRef = useRef({ tracks, armedTrackId, sessionTracks, bpm, keySignature });
  latestRef.current = { tracks, armedTrackId, sessionTracks, bpm, keySignature };

  const liveTracks = sessionTracks || tracks;
  const armedTrack = useMemo(() => liveTracks.find((track) => track.id === armedTrackId) || null, [liveTracks, armedTrackId]);
  const armedType = recordableTrackType(armedTrack);
  const targetClip = armedTrack ? targetPatternClip(armedTrack, Math.floor(playheadBar || 0))
    || (armedTrack.clips || []).find((clip) => clip.isPattern && clip.patternType === armedType) : null;

  const setClickEnabled = useCallback((enabled) => {
    const next = Boolean(enabled); setClickEnabledState(next);
    try { localStorage.setItem(CLICK_KEY, next ? '1' : '0'); } catch { /* storage is optional */ }
  }, []);
  const setAutoQuantize = useCallback((enabled) => {
    const next = Boolean(enabled); setAutoQuantizeState(next);
    try { localStorage.setItem(AUTO_QUANTIZE_KEY, next ? '1' : '0'); } catch { /* storage is optional */ }
  }, []);
  const setCountInEnabled = useCallback((enabled) => {
    const next = Boolean(enabled); setCountInEnabledState(next);
    try { localStorage.setItem(COUNT_IN_KEY, next ? '1' : '0'); } catch { /* storage is optional */ }
  }, []);

  const stopAudition = useCallback((token, at) => {
    const handle = auditionsRef.current.get(token); handle?.stop?.(at); auditionsRef.current.delete(token);
  }, []);

  const finishHeld = useCallback((token, at) => {
    const held = heldRef.current.get(token); if (!held) return;
    const take = takeRef.current;
    if (take && held.started) take.events.push({ ...held.event, length: Math.max(1 / 64, (at - held.startedAt) / take.beatSeconds) });
    heldRef.current.delete(token);
  }, []);

  const noteOff = useCallback((pitch, { token = `midi-${pitch}` } = {}) => {
    const ctx = getAudioContext();
    if (pendingRef.current.has(token)) { pendingRef.current.get(token).released = true; finishHeld(token, ctx.currentTime); return; }
    if (monoTokensRef.current.has(token)) {
      const sounding = monoTokensRef.current.get(token); monoTokensRef.current.delete(token);
      auditionsRef.current.get('mono')?.noteOff?.(sounding, ctx.currentTime);
    } else stopAudition(token, ctx.currentTime);
    finishHeld(token, ctx.currentTime);
  }, [finishHeld, stopAudition]);

  const noteOn = useCallback(async (pitch, velocity = 0.8, { token = `midi-${pitch}`, key = null } = {}) => {
    const currentTracks = latestRef.current.sessionTracks || latestRef.current.tracks;
    const track = currentTracks.find((item) => item.id === latestRef.current.armedTrackId);
    const type = recordableTrackType(track); if (!track || !type) return false;
    const pending = { released: false }; pendingRef.current.set(token, pending);
    const ctx = getAudioContext(); if (ctx.state === 'suspended') await ctx.resume();
    const clip = targetPatternClip(track, Math.floor(playheadBar || 0)) || track.clips.find((item) => item.isPattern && item.patternType === type);
    const pattern = clip?.pattern;
    const drumRow = type === 'drums' ? (key ? DRUM_KEY_ROWS[key] : DRUM_MIDI_ROWS[pitch]) : null;
    if (type === 'drums') {
      const row = pattern?.rows?.find((item) => item.rowId === drumRow);
      const buffer = row?.soundId ? await getSoundBuffer(row.soundId).catch(() => null) : null;
      if (pendingRef.current.get(token) === pending) pendingRef.current.delete(token);
      if (buffer) auditionsRef.current.set(token, playDrumHit(ctx, getMasterGain(), buffer, { when: ctx.currentTime, velocity, volume: row.volume }));
    } else if (pattern) {
      const parsedKey = parseSessionKey(latestRef.current.keySignature);
      const soundingPitch = pattern.lockToKey && parsedKey ? foldPitchToKey(Number(pitch), parsedKey) : Number(pitch);
      let voiceSpec = pattern;
      if (type === 'sampler') voiceSpec = { ...pattern, buffer: pattern.sampleId ? await getSoundBuffer(pattern.sampleId).catch(() => null) : null };
      if (pendingRef.current.get(token) === pending) pendingRef.current.delete(token);
      if (pending.released) {
        // The key was let go while the sound loaded: play it as a short tap instead of a stuck note.
        const tap = playNote(ctx, getMasterGain(), { ...voiceSpec, mono: false }, { pitch: soundingPitch, velocity, when: ctx.currentTime, duration: 0.25 });
        if (tap) setTimeout(() => tap.stop?.(), 2000);
        return true;
      }
      const voiceKey = liveVoiceKey(type, clip);
      const staleMono = auditionsRef.current.get('mono');
      if (staleMono && (!voiceSpec.mono || staleMono.voiceKey !== voiceKey)) {
        staleMono.stop?.(ctx.currentTime); auditionsRef.current.delete('mono'); monoTokensRef.current.clear();
      }
      if (voiceSpec.mono) {
        let mono = auditionsRef.current.get('mono');
        if (!mono && (type !== 'sampler' || voiceSpec.buffer)) { mono = new MonoVoice(ctx, getMasterGain(), voiceSpec); mono.voiceKey = voiceKey; auditionsRef.current.set('mono', mono); }
        if (mono) { mono.noteOn(soundingPitch, velocity, ctx.currentTime); monoTokensRef.current.set(token, soundingPitch); }
      } else {
        stopAudition(token, ctx.currentTime);
        const handle = playNote(ctx, getMasterGain(), voiceSpec, { pitch: soundingPitch, velocity, when: ctx.currentTime, duration: 30 });
        if (handle) auditionsRef.current.set(token, handle);
      }
      pitch = soundingPitch;
    }
    if (pendingRef.current.get(token) === pending) pendingRef.current.delete(token);
    const take = takeRef.current;
    if (take && ctx.currentTime >= take.recordStart) {
      const position = clipRelativeBeat({ contextTime: ctx.currentTime, recordStart: take.recordStart, beatSeconds: take.beatSeconds, recordBar: take.recordBar, clip: take.target.clip });
      if (type === 'drums') take.events.push({ id: `hit-${Date.now()}-${take.events.length}`, pitch, row: drumRow, velocity, ...position });
      else heldRef.current.set(token, { started: true, startedAt: ctx.currentTime, event: { id: `note-${Date.now()}-${take.events.length}`, pitch, velocity, ...position } });
    }
    return true;
  }, [playheadBar, stopAudition]);

  const stopRecording = useCallback(({ stopTransport = true } = {}) => {
    const take = takeRef.current; if (!take) return false;
    const ctx = getAudioContext();
    for (const token of [...heldRef.current.keys()]) finishHeld(token, ctx.currentTime);
    const result = applyRecordingEvents(take.target.tracks, take.target, take.events, { autoQuantize, snap: recordSnap });
    pushState({ tracks: result.tracks, sectionMarkers });
    if (result.linkedCopies > 1) onNotice?.(`Recording into a linked pattern (${result.linkedCopies} copies)`);
    if (result.dropped) onNotice?.('Notes past the end of the pattern were not kept.');
    for (const source of take.countIn || []) { try { source.onended = null; source.stop(); source.disconnect(); } catch {} }
    takeRef.current = null; setSessionTracks(null); setRecording(false);
    if (stopTransport) stopPlayback();
    onPlayingChange?.(false, false);
    return true;
  }, [autoQuantize, finishHeld, onNotice, onPlayingChange, pushState, sectionMarkers]);

  const toggleRecord = useCallback(async () => {
    if (takeRef.current) { stopRecording(); return; }
    if (!armedTrackId && countTrackSlots(tracks, classSession) >= maxTracks) { onNotice?.(`You can use up to ${maxTracks} tracks. Delete one before recording.`); return; }
    await loadProducerSounds();
    const recordBar = Math.floor(playheadBar || 0);
    const firstCategory = getOneShotCategories()[0]?.id;
    const firstSound = getOneShots(firstCategory)[0] || getOneShots()[0];
    const target = prepareRecordingTarget({ tracks, armedTrackId, recordBar, loopEnabled, loopRegion, kit: getKits()[0] || null,
      sampleDefaults: firstSound ? samplerDefaultsForSound(firstSound) : null });
    const linkedCopies = target.tracks.reduce((count, track) => count + track.clips.filter((clip) => clip.isPattern && clip.loop_id === target.loopId).length, 0);
    if (linkedCopies > 1) onNotice?.(`Recording into a linked pattern (${linkedCopies} copies)`);
    setArmedTrackId(target.trackId); setSessionTracks(target.tracks);
    const ctx = getAudioContext(); if (ctx.state === 'suspended') await ctx.resume();
    const beatSeconds = 60 / bpm; const barSeconds = secondsPerBar(bpm); const recordStart = ctx.currentTime + (countInEnabled ? barSeconds : 0);
    const countIn = countInEnabled ? scheduleMetronomeBeats(ctx, getMasterGain(), { startAt: ctx.currentTime, beatSeconds, firstBeat: 0, beats: 4 }) : [];
    takeRef.current = { target, events: [], recordStart, beatSeconds, recordBar, countIn };
    setRecording(true);
    startPlayback(target.tracks, bpm, totalBars, recordBar, onPlayheadUpdate, () => { stopRecording({ stopTransport: false }); onPlaybackEnd?.(); },
      { enabled: loopEnabled, region: loopRegion }, { startAt: recordStart, metronome: clickEnabled });
    onPlayingChange?.(true, false);
  }, [armedTrackId, bpm, classSession, clickEnabled, countInEnabled, loopEnabled, loopRegion, maxTracks, onNotice, onPlaybackEnd, onPlayheadUpdate, onPlayingChange, playheadBar, stopRecording, totalBars, tracks]);

  const connectMidi = useCallback(async () => {
    if (!navigator.requestMIDIAccess) return false;
    const access = midiAccessRef.current || await navigator.requestMIDIAccess(); midiAccessRef.current = access;
    const attach = () => {
      for (const input of access.inputs.values()) input.onmidimessage = (event) => {
        const message = midiMessage(event.data); if (!message) return;
        if (message.type === 'on') noteOn(message.pitch, message.velocity, { token: `midi-${message.pitch}` });
        else noteOff(message.pitch, { token: `midi-${message.pitch}` });
      };
      const connected = [...access.inputs.values()].find((input) => input.state !== 'disconnected');
      setMidiName(connected?.name || '');
    };
    access.onstatechange = attach; attach(); return true;
  }, [noteOff, noteOn]);

  useEffect(() => {
    if (!dockOpen) return undefined;
    const down = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTextEntry(event.target)) return;
      const key = String(event.key || '').toLowerCase();
      if (event.repeat || heldKeys.has(key)) return;
      if (key === 'z' || key === 'x') { event.preventDefault(); setOctave((value) => Math.max(0, Math.min(7, value + (key === 'x' ? 1 : -1)))); return; }
      if (key === 'c' || key === 'v') { event.preventDefault(); setVelocityLevel((value) => Math.max(1, Math.min(5, value + (key === 'v' ? 1 : -1)))); return; }
      if (!Object.prototype.hasOwnProperty.call(DOCK_NOTE_OFFSETS, key)) return;
      event.preventDefault();
      setHeldKeys((previous) => new Set(previous).add(key));
      noteOn((octave + 2) * 12 + DOCK_NOTE_OFFSETS[key], velocityLevel / 5, { token: `key-${key}`, key });
    };
    const up = (event) => {
      const key = String(event.key || '').toLowerCase(); if (!heldKeys.has(key)) return;
      event.preventDefault(); setHeldKeys((previous) => { const next = new Set(previous); next.delete(key); return next; });
      noteOff((octave + 2) * 12 + (DOCK_NOTE_OFFSETS[key] || 0), { token: `key-${key}` });
    };
    window.addEventListener('keydown', down); window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [dockOpen, heldKeys, noteOff, noteOn, octave, velocityLevel]);

  const armedVoiceKey = liveVoiceKey(armedType, targetClip);
  useEffect(() => {
    const ctx = auditionsRef.current.size ? getAudioContext() : null;
    for (const handle of auditionsRef.current.values()) handle?.stop?.(ctx?.currentTime);
    auditionsRef.current.clear(); monoTokensRef.current.clear();
  }, [armedVoiceKey]);

  useEffect(() => () => {
    for (const handle of auditionsRef.current.values()) handle?.stop?.();
  }, []);

  return { armedTrackId, setArmedTrackId, armedTrack, armedType, liveTracks, dockOpen, setDockOpen, octave, setOctave, velocityLevel, heldKeys, midiName,
    midiSupported: typeof navigator !== 'undefined' && Boolean(navigator.requestMIDIAccess), connectMidi, noteOn, noteOff, recording,
    recordingClipId: takeRef.current?.target?.clipId || null, toggleRecord, stopRecording, clickEnabled, setClickEnabled,
    autoQuantize, setAutoQuantize, countInEnabled, setCountInEnabled, recordSnap };
}

export default useLiveInput;
